import { getCSRFToken } from '@cartwheel/api'
import * as React from 'react'

// Types
interface PayPeriodProps {
  payPeriod: {
    id: number
    start_date: string
    end_date: string
    bonus_payout_period: boolean
    finalized_at: string | null
    created_at: string
    metadata: {
      recipient_email?: string
      jobs_completed?: number
      jobs_failed?: number
      jobs_total?: number
    }
  }
  summary: {
    total_clinicians: number
    total_gross_pay: number
    hourly_count: number
    contractor_count: number
    full_time_count: number
    hourly_total: number
    contractor_total: number
    full_time_total: number
  }
  payStubs: PayStub[]
  navigation: {
    back_to_payroll_path: string
    previous_pay_period_path: string | null
    next_pay_period_path: string | null
  }
}

interface PayStub {
  id: number
  clinician_id: number
  clinician_name: string
  clinician_email: string
  employment_type: string
  gross_pay: number
  total_hours: number
  admin_hours: number | null
  pto_hours: number | null
  care_bonus: number
  mission_bonus: number
  created_at: string
}

export default function PayPeriodDetails({
  payPeriod,
  summary,
  payStubs,
  navigation,
}: PayPeriodProps) {
  const [searchTerm, setSearchTerm] = React.useState('')
  const [employmentTypeFilter, setEmploymentTypeFilter] = React.useState<string>('all')
  const [showDetails, setShowDetails] = React.useState(false)
  const [isRollingBack, setIsRollingBack] = React.useState(false)

  // Calculate if rollback is allowed (within 48 hours of creation)
  const canRollback = React.useMemo(() => {
    const createdAt = new Date(payPeriod.created_at)
    const now = new Date()
    const hoursSinceCreation = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60)

    return hoursSinceCreation <= 48
  }, [payPeriod])

  const handleRollback = async () => {
    const confirmMessage = `Are you sure you want to rollback the pay period from ${payPeriod.start_date} to ${payPeriod.end_date}? This will delete all pay stubs and the pay period itself.`

    if (!window.confirm(confirmMessage)) return

    setIsRollingBack(true)

    try {
      const response = await fetch(`/pay_periods/${payPeriod.id}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCSRFToken(),
        },
      })

      if (response.ok) {
        const data = await response.json()
        // Redirect to payroll page
        window.location.href = data.redirect_url || '/payroll'
      } else {
        const errorData = await response.json()
        alert(`Failed to rollback pay period: ${errorData.error || 'Unknown error'}`)
        setIsRollingBack(false)
      }
    } catch (error) {
      alert(`Failed to rollback pay period: ${error}`)
      setIsRollingBack(false)
    }
  }

  // Filter pay stubs based on search and employment type
  const filteredPayStubs = React.useMemo(() => {
    return payStubs.filter((stub) => {
      const matchesSearch =
        searchTerm === '' ||
        stub.clinician_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        stub.clinician_email.toLowerCase().includes(searchTerm.toLowerCase())

      const matchesType =
        employmentTypeFilter === 'all' || stub.employment_type === employmentTypeFilter

      return matchesSearch && matchesType
    })
  }, [payStubs, searchTerm, employmentTypeFilter])

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getEmploymentTypeColor = (type: string) => {
    switch (type) {
      case 'full_time':
        return 'bg-blue-100 text-blue-800'
      case 'hourly':
        return 'bg-green-100 text-green-800'
      case 'contractor':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getEmploymentTypeLabel = (type: string) => {
    switch (type) {
      case 'full_time':
        return 'Full Time'
      case 'hourly':
        return 'Hourly'
      case 'contractor':
        return 'Contractor'
      default:
        return type
    }
  }

  return (
    <div className="p-6 mx-auto max-w-7xl">
      {/* Header with Navigation */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <a
            href={navigation.back_to_payroll_path}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            ← Back to Payroll
          </a>
          <div className="flex gap-2">
            {navigation.previous_pay_period_path && (
              <a
                href={navigation.previous_pay_period_path}
                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                ← Previous Period
              </a>
            )}
            {navigation.next_pay_period_path && (
              <a
                href={navigation.next_pay_period_path}
                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50"
              >
                Next Period →
              </a>
            )}
            <button
              type="button"
              onClick={handleRollback}
              disabled={!canRollback || isRollingBack}
              className={`px-3 py-1 text-sm rounded transition-colors ${
                canRollback
                  ? 'text-white bg-red-600 hover:bg-red-700'
                  : 'text-gray-400 bg-gray-200 cursor-not-allowed'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              title={
                canRollback ? undefined : 'Rollback is only available within 48 hours of creation'
              }
            >
              {isRollingBack ? 'Rolling back...' : 'Rollback Pay Period'}
            </button>
          </div>
        </div>

        <h1 className="text-3xl font-bold text-gray-900">Pay Period Details</h1>
        <p className="mt-2 text-gray-600">
          {formatDate(payPeriod.start_date)} - {formatDate(payPeriod.end_date)}
          {payPeriod.bonus_payout_period && (
            <span className="px-2 py-1 ml-2 text-xs font-medium text-yellow-800 bg-yellow-100 rounded">
              Bonus Period
            </span>
          )}
        </p>
      </div>

      {/* Status and Metadata */}
      <div className="p-4 mb-6 bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <span className="text-sm font-medium text-gray-500">Status</span>
            <div className="mt-1">
              {payPeriod.finalized_at ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  ✅ Finalized
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  ⏳ In Progress
                </span>
              )}
            </div>
          </div>

          {payPeriod.finalized_at && (
            <div>
              <span className="text-sm font-medium text-gray-500">Finalized At</span>
              <div className="mt-1 text-gray-900">{formatDateTime(payPeriod.finalized_at)}</div>
            </div>
          )}

          {payPeriod.metadata.recipient_email && (
            <div>
              <span className="text-sm font-medium text-gray-500">Report Sent To</span>
              <div className="mt-1 text-gray-900">{payPeriod.metadata.recipient_email}</div>
            </div>
          )}
        </div>

        {payPeriod.metadata.jobs_total && (
          <div className="pt-4 mt-4 border-t border-gray-200">
            <span className="text-sm font-medium text-gray-500">Processing Stats</span>
            <div className="flex gap-4 mt-2 text-sm">
              <span className="text-green-600">
                ✓ {payPeriod.metadata.jobs_completed || 0} Completed
              </span>
              {payPeriod.metadata.jobs_failed > 0 && (
                <span className="text-red-600">✗ {payPeriod.metadata.jobs_failed} Failed</span>
              )}
              <span className="text-gray-600">Total: {payPeriod.metadata.jobs_total}</span>
            </div>
          </div>
        )}
      </div>

      {/* Summary Statistics */}
      <div className="p-6 mb-6 bg-white border border-gray-200 rounded-lg shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Summary</h2>

        <div className="grid grid-cols-2 gap-4 mb-6 md:grid-cols-4">
          <div className="p-4 rounded-lg bg-gray-50">
            <div className="text-2xl font-bold text-gray-900">{summary.total_clinicians}</div>
            <div className="text-sm text-gray-600">Total Clinicians</div>
          </div>
          <div className="p-4 rounded-lg bg-blue-50">
            <div className="text-2xl font-bold text-blue-900">
              {formatCurrency(summary.total_gross_pay)}
            </div>
            <div className="text-sm text-gray-600">Total Gross Pay</div>
          </div>
          <div className="p-4 rounded-lg bg-green-50">
            <div className="text-2xl font-bold text-green-900">
              {formatCurrency(summary.total_gross_pay / summary.total_clinicians || 0)}
            </div>
            <div className="text-sm text-gray-600">Average Pay</div>
          </div>
          <div className="p-4 rounded-lg bg-purple-50">
            <button
              type="button"
              onClick={() => setShowDetails(!showDetails)}
              className="w-full text-left"
            >
              <div className="text-2xl font-bold text-purple-900">{showDetails ? '−' : '+'}</div>
              <div className="text-sm text-gray-600">View Details</div>
            </button>
          </div>
        </div>

        {showDetails && (
          <div className="pt-4 border-t border-gray-200">
            <h3 className="mb-3 text-sm font-medium text-gray-700">By Employment Type</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {summary.full_time_count > 0 && (
                <div className="p-3 border rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600">Full Time</span>
                    <span
                      className={`px-2 py-1 text-xs rounded ${getEmploymentTypeColor('full_time')}`}
                    >
                      {summary.full_time_count} clinicians
                    </span>
                  </div>
                  <div className="text-lg font-semibold text-gray-900">
                    {formatCurrency(summary.full_time_total)}
                  </div>
                </div>
              )}

              {summary.hourly_count > 0 && (
                <div className="p-3 border rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600">Hourly</span>
                    <span
                      className={`px-2 py-1 text-xs rounded ${getEmploymentTypeColor('hourly')}`}
                    >
                      {summary.hourly_count} clinicians
                    </span>
                  </div>
                  <div className="text-lg font-semibold text-gray-900">
                    {formatCurrency(summary.hourly_total)}
                  </div>
                </div>
              )}

              {summary.contractor_count > 0 && (
                <div className="p-3 border rounded-lg">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600">Contractor</span>
                    <span
                      className={`px-2 py-1 text-xs rounded ${getEmploymentTypeColor('contractor')}`}
                    >
                      {summary.contractor_count} clinicians
                    </span>
                  </div>
                  <div className="text-lg font-semibold text-gray-900">
                    {formatCurrency(summary.contractor_total)}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Pay Stubs List */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Pay Stubs ({filteredPayStubs.length} of {payStubs.length})
            </h2>

            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                type="text"
                placeholder="Search clinicians..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />

              <select
                value={employmentTypeFilter}
                onChange={(e) => setEmploymentTypeFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Types</option>
                <option value="full_time">Full Time</option>
                <option value="hourly">Hourly</option>
                <option value="contractor">Contractor</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Clinician
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                  Type
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-right text-gray-500 uppercase">
                  Total Hours
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-right text-gray-500 uppercase">
                  Gross Pay
                </th>
                <th className="px-6 py-3 text-xs font-medium tracking-wider text-right text-gray-500 uppercase">
                  Care Bonus
                </th>
                {payPeriod.bonus_payout_period && (
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-right text-gray-500 uppercase">
                    Mission Bonus
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPayStubs.length > 0 ? (
                filteredPayStubs.map((stub) => (
                  <tr key={stub.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {stub.clinician_name}
                        </div>
                        <div className="text-xs text-gray-500">{stub.clinician_email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${getEmploymentTypeColor(stub.employment_type)}`}
                      >
                        {getEmploymentTypeLabel(stub.employment_type)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900 whitespace-nowrap">
                      {stub.total_hours != null ? stub.total_hours.toFixed(2) : '—'}
                    </td>
                    <td className="px-6 py-4 text-sm font-medium text-right text-gray-900 whitespace-nowrap">
                      {formatCurrency(stub.gross_pay)}
                    </td>
                    <td className="px-6 py-4 text-sm text-right text-gray-900 whitespace-nowrap">
                      {stub.care_bonus > 0 ? formatCurrency(stub.care_bonus) : '—'}
                    </td>
                    {payPeriod.bonus_payout_period && (
                      <td className="px-6 py-4 text-sm text-right text-gray-900 whitespace-nowrap">
                        {stub.mission_bonus > 0 ? formatCurrency(stub.mission_bonus) : '—'}
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={payPeriod.bonus_payout_period ? 6 : 5}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No pay stubs found matching your criteria
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
